import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import useWorkspaceSubscriptionFeatures from "../../hooks/useWorkspaceSubscriptionFeatures";
import {
  hasSubscriptionFeature,
  SUBSCRIPTION_FEATURE_KEYS,
} from "../../workspaces/subscriptionFeatures";
import { CARE_VIEW_INCIDENTS, hasWorkspacePrivilege } from "../../utils/carePrivileges";

const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const joinName = (...values) => values.map((value) => String(value || "").trim()).filter(Boolean).join(" ");

const residentName = (resident = {}) =>
  firstText(
    resident.name,
    resident.fullName,
    resident.residentName,
    joinName(resident.firstName, resident.middleName, resident.lastName),
    resident.email,
    "Unnamed resident",
  );

const residentId = (resident = {}) => resident.id || resident.residentId || resident.clientId || "";

const residentEmail = (resident = {}) => firstText(resident.email, resident.primaryContactEmail);

const careProfileId = (profile = {}) => profile.id || profile.careProfileId || profile.profileId || "";

const careProfileName = (profile = {}) => {
  const user = profile.userProfile || profile.user || {};
  const base = profile.profile || profile.profileData || {};
  return firstText(
    user.name,
    user.fullName,
    profile.name,
    profile.fullName,
    joinName(profile.firstName, profile.middleName, profile.lastName),
    base.fullName,
    joinName(base.firstName, base.middleName, base.lastName),
    joinName(user.firstName, user.middleName, user.lastName),
    profile.userName,
    careProfileEmail(profile),
    "Care staff",
  );
};

const careProfileEmail = (profile = {}) => {
  const user = profile.userProfile || profile.user || {};
  const base = profile.profile || profile.profileData || {};
  return firstText(user.email, base.email, profile.email);
};

const actionCardsFor = (canManageWorkspace, hasFeature, canViewIncidents) => {
  const commonActions = [
    hasFeature("medicationManagement") ? ["ti-pill", "eMAR", "Review resident medications and document medication administration.", "amber", "/resident-medications"] : null,
    hasFeature("assessments") ? ["ti-notes", "Daily Notes", "Record and review daily resident observations and actions taken.", "blue", "/daily-notes"] : null,
    hasFeature("carePlans") ? ["ti-list-check", "ADLs", "Schedule and review residents' daily living activities and routines.", "blue", "/resident-adls"] : null,
    hasFeature("attendance") ? ["ti-clock", "My Clocking", "Clock in, clock out, and review your attendance status.", "blue", "/my-attendance"] : null,
    hasFeature("residents") ? ["ti-users", "Residents", "Review resident profiles, contacts, notes, and care documents.", "blue", "/residents"] : null,
    hasFeature("schedules") ? ["ti-calendar", "My Schedule", "See your shifts and care coverage for the selected workspace.", "amber", "/my-schedule"] : null,
    hasFeature("timesheets") ? ["ti-receipt", "My Timesheets", "Create or review your own timesheet records.", "purple", "/my-timesheet"] : null,
    ["ti-user-heart", "My Care Profile", "Keep your care worker profile and onboarding details current.", "amber", "/care-profile"],
  ].filter(Boolean);

  if (!canManageWorkspace) return commonActions;

  return [
    hasFeature("medicationManagement") ? ["ti-pill", "eMAR", "Review resident medications and document medication administration.", "amber", "/resident-medications"] : null,
    hasFeature("assessments") ? ["ti-notes", "Daily Notes", "Record and review daily resident observations and actions taken.", "blue", "/daily-notes"] : null,
    hasFeature("carePlans") ? ["ti-list-check", "ADLs", "Schedule and review residents' daily living activities and routines.", "blue", "/resident-adls"] : null,
    hasFeature("attendance") ? ["ti-clock", "My Clocking", "Clock in, clock out, and review your own attendance status.", "blue", "/my-attendance"] : null,
    hasFeature("residents") ? ["ti-users", "Residents", "Review resident profiles, contacts, notes, and care documents.", "blue", "/residents"] : null,
    hasFeature("schedules") ? ["ti-calendar", "My Schedule", "See your own shifts in this workspace.", "amber", "/my-schedule"] : null,
    hasFeature("schedules") ? ["ti-calendar-plus", "Schedules", "Plan staff shifts and review care coverage.", "amber", "/schedules"] : null,
    hasFeature("assessments") ? ["ti-clipboard-check", "Resident Care Processes", "Create or continue each resident's care process.", "purple", "/assessments"] : null,
    hasFeature("incidentReporting") && canViewIncidents ? ["ti-alert-triangle", "Incident Reporting", "Capture incidents, actions taken, notifications, and follow-up.", "amber", "/incidents"] : null,
    hasFeature("attendance") ? ["ti-clock-check", "Staff ClockInOut", "Review attendance records and current clock status.", "blue", "/attendance"] : null,
    hasFeature("timesheets") ? ["ti-receipt", "My Timesheets", "Create or review your own timesheet records.", "purple", "/my-timesheet"] : null,
    hasFeature("timesheets") ? ["ti-file-dollar", "Timesheets", "Review, approve, and manage staff timesheets.", "purple", "/timesheets"] : null,
    // hasFeature("rnDelegationWorkflows") ? ["ti-stethoscope", "RN Delegation", "Manage delegated nursing tasks, reviews, training, and signatures.", "blue", "/rn-delegations"] : null,
    // hasFeature("mealPlanning") ? ["ti-tools-kitchen-2", "Care Planning", "Plan meals, activities, ADL routines, and care tasks.", "amber", "/care-planning"] : null,
    hasFeature("weeklyCaregiverHoursTracking") ? ["ti-clock-dollar", "Hours Operations", "Review caregiver hours, overtime, corrections, and disputes.", "purple", "/hours-operations"] : null,
    hasFeature("cbhsSupportiveSupervisionTracking") ? ["ti-user-shield", "CBHS Supervision", "Track supportive supervision and monthly CBHS report readiness.", "purple", "/cbhs"] : null,
    hasFeature("documents") ? ["ti-files", "Documents", "Upload and manage public or internal workspace documents.", "amber", "/downloads"] : null,
    hasFeature("secureCareTeamDocumentExchange") ? ["ti-mail-lock", "Secure Exchange", "Track secure care-team email, fax, and document exchange.", "blue", "/secure-care-team-exchange"] : null,
    // hasFeature("familyPortal") ? ["ti-users-group", "Family Portal", "Manage family updates, shared documents, and signature requests.", "amber", "/family-portal"] : null,
  ].filter(Boolean);
};

export default function HomePage() {
  const { user, careRoles = [], privileges = [] } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId } = useWorkspace();
  const [residentSearch, setResidentSearch] = useState("");
  const [careStaffSearch, setCareStaffSearch] = useState("");
  const [residentSearchResults, setResidentSearchResults] = useState([]);
  const [careStaffSearchResults, setCareStaffSearchResults] = useState([]);
  const [residentSearchLoading, setResidentSearchLoading] = useState(false);
  const [careStaffSearchLoading, setCareStaffSearchLoading] = useState(false);
  const [residentSearchError, setResidentSearchError] = useState("");
  const [careStaffSearchError, setCareStaffSearchError] = useState("");

  const normalizedCareRoles = useMemo(() => careRoles.map(normalizeRole), [careRoles]);
  const canManageWorkspace = normalizedCareRoles.includes("care_admin") || normalizedCareRoles.includes("care_owner");
  const canViewIncidents = hasWorkspacePrivilege({ privileges, user, workspace: selectedWorkspace }, CARE_VIEW_INCIDENTS);
  const { features: subscriptionFeatures, tier: subscriptionTierName } = useWorkspaceSubscriptionFeatures();
  const hasFeature = useMemo(
    () => (feature) => hasSubscriptionFeature(subscriptionFeatures, feature),
    [subscriptionFeatures],
  );
  const featureKeys = SUBSCRIPTION_FEATURE_KEYS;

  useEffect(() => {
    const query = residentSearch.trim();
    if (!selectedWorkspaceId || query.length < 3) {
      setResidentSearchResults([]);
      setResidentSearchLoading(false);
      setResidentSearchError("");
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      setResidentSearchLoading(true);
      setResidentSearchError("");
      careService.residents
        .quickSearch({
          wid: selectedWorkspaceId,
          workspaceId: selectedWorkspaceId,
          query,
          size: 8,
        })
        .then((payload) => {
          if (!active) return;
          setResidentSearchResults(pageItems(payload).slice(0, 8));
        })
        .catch((err) => {
          if (!active) return;
          setResidentSearchResults([]);
          setResidentSearchError(err?.response?.data?.message || err?.message || "Unable to search residents.");
        })
        .finally(() => {
          if (active) setResidentSearchLoading(false);
        });
    }, 280);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [residentSearch, selectedWorkspaceId]);

  useEffect(() => {
    const query = careStaffSearch.trim();
    if (!selectedWorkspaceId || query.length < 3) {
      setCareStaffSearchResults([]);
      setCareStaffSearchLoading(false);
      setCareStaffSearchError("");
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      setCareStaffSearchLoading(true);
      setCareStaffSearchError("");
      careService.careProfiles
        .quickSearch({
          wid: selectedWorkspaceId,
          workspaceId: selectedWorkspaceId,
          query,
          size: 8,
        })
        .then((payload) => {
          if (!active) return;
          setCareStaffSearchResults(pageItems(payload).slice(0, 8));
        })
        .catch((err) => {
          if (!active) return;
          setCareStaffSearchResults([]);
          setCareStaffSearchError(err?.response?.data?.message || err?.message || "Unable to search care staff.");
        })
        .finally(() => {
          if (active) setCareStaffSearchLoading(false);
        });
    }, 280);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [careStaffSearch, selectedWorkspaceId]);

  const actions = actionCardsFor(canManageWorkspace, hasFeature, canViewIncidents);
  const residentSearchReady = residentSearch.trim().length >= 3;
  const careStaffSearchReady = careStaffSearch.trim().length >= 3;

  return (
    <CareShell className="dashboard-content workspace care-home-page">
      <section className="home-action-panel">
        <div className="home-search-grid" aria-label="Resident and care staff search">
          {hasFeature(featureKeys.residents) ? (
            <section className="home-search-card">
              <div className="home-search-card-head">
                <span className="round-icon blue"><i className="ti ti-user-search" /></span>
                <div>
                  <strong>Resident Search</strong>
                  <small>Find residents by name or email</small>
                </div>
              </div>
              <label className="home-search-field">
                <i className="ti ti-search" />
                <input value={residentSearch} onChange={(event) => setResidentSearch(event.target.value)} placeholder="Search residents" />
              </label>
              <div className="home-search-results">
                {!residentSearchReady ? <p>Type at least 3 characters to search residents.</p> : residentSearchLoading ? <p>Searching residents...</p> : residentSearchError ? <p>{residentSearchError}</p> : residentSearchResults.length ? residentSearchResults.map((resident) => {
                  const id = residentId(resident);
                  return (
                    <Link className="home-search-result-row" to={id ? `/residents/${id}` : "/residents"} key={id || residentName(resident)}>
                      <span>{residentName(resident)}</span>
                      <small>{residentEmail(resident) || "No email added"}</small>
                      <i className="ti ti-chevron-right" />
                    </Link>
                  );
                }) : <p>No residents found.</p>}
              </div>
            </section>
          ) : null}
          <section className="home-search-card">
            <div className="home-search-card-head">
              <span className="round-icon amber"><i className="ti ti-users-group" /></span>
              <div>
                <strong>Care Staff Search</strong>
                <small>Find care givers by name or email</small>
              </div>
            </div>
            <label className="home-search-field">
              <i className="ti ti-search" />
              <input value={careStaffSearch} onChange={(event) => setCareStaffSearch(event.target.value)} placeholder="Search care staff" />
            </label>
            <div className="home-search-results">
              {!careStaffSearchReady ? <p>Type at least 3 characters to search care staff.</p> : careStaffSearchLoading ? <p>Searching care staff...</p> : careStaffSearchError ? <p>{careStaffSearchError}</p> : careStaffSearchResults.length ? careStaffSearchResults.map((profile) => {
                const id = careProfileId(profile);
                return (
                  <Link className="home-search-result-row" to={id ? `/staff/${id}` : "/care-profile"} key={id || careProfileName(profile)}>
                    <span>{careProfileName(profile)}</span>
                    <small>{careProfileEmail(profile) || "No email added"}</small>
                    <i className="ti ti-chevron-right" />
                  </Link>
                );
              }) : <p>No care staff found.</p>}
            </div>
          </section>
        </div>
        <div className="home-section-head">
          <h2>Quick actions</h2>
          <span>{canManageWorkspace ? "Workspace operations" : "My care tools"}</span>
        </div>
        {actions.length ? (
          <div className="action-grid">
            {actions.map(([icon, title, text, tone, to]) => (
              <Link className="action-card" to={to} key={title}>
                <span className={`round-icon ${tone}`}><i className={`ti ${icon}`} /></span>
                <div><h2>{title}</h2><p>{text}</p></div>
                <i className="ti ti-arrow-right arrow" />
              </Link>
            ))}
          </div>
        ) : (
          <Panel title="Subscription features">
            <p>Your {subscriptionTierName.toLowerCase()} plan does not include additional care tools for this workspace.</p>
            {canManageWorkspace ? <Link className="text-action" to="/workspaces/current/subscriptions">Manage subscription <i className="ti ti-arrow-right" /></Link> : null}
          </Panel>
        )}
      </section>

    </CareShell>
  );
}
